import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Loader2, Sparkles, Crown, Heart, ArrowRight, Star, Dumbbell, Trophy } from 'lucide-react';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WompiSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, failed, pending, needs_registration
  const [attempts, setAttempts] = useState(0);
  const [message, setMessage] = useState('');
  const [userName, setUserName] = useState('');
  const [transactionEmail, setTransactionEmail] = useState('');
  const [productType, setProductType] = useState(''); // plan, challenge, academia
  const [challengeId, setChallengeId] = useState('');
  const [challengeTitle, setChallengeTitle] = useState('');
  
  // Wompi puede enviar el ID de diferentes formas
  const reference = searchParams.get('reference');
  const wompiId = searchParams.get('id');
  const transactionId = searchParams.get('transaction_id');
  
  // Usar cualquier identificador disponible
  const paymentIdentifier = reference || wompiId || transactionId;
  
  useEffect(() => {
    // Obtener nombre del usuario
    const token = localStorage.getItem('session_token');
    if (token) {
      axios.get(`${BACKEND_URL}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setUserName(res.data.name?.split(' ')[0] || ''))
        .catch(() => {});
    }
  }, []);

  // Redirección automática INMEDIATA cuando necesita crear cuenta
  useEffect(() => {
    if (status === 'needs_registration') {
      // Redirigir inmediatamente, sin esperar
      navigate(`/complete-registration?reference=${reference || ''}&email=${transactionEmail || ''}&challenge_id=${challengeId || ''}&product_type=${productType || ''}`);
    }
  }, [status, reference, transactionEmail, challengeId, productType, navigate]);
  
  useEffect(() => {
    // Si no hay ningún identificador, intentar verificar el último pago del usuario
    const verifyPayment = async () => {
      const token = localStorage.getItem('session_token');
      
      try {
        let url;
        
        if (paymentIdentifier) {
          // Tenemos un identificador - verificar ese pago específico
          url = `${BACKEND_URL}/api/payments/wompi/verify/${paymentIdentifier}`;
          if (wompiId && wompiId !== paymentIdentifier) {
            url += `?wompi_id=${wompiId}`;
          }
        } else {
          // No hay identificador - verificar último pago pendiente del usuario
          url = `${BACKEND_URL}/api/payments/wompi/verify-latest`;
        }
        
        console.log('Verificando pago Wompi:', { reference, wompiId, transactionId, url });
        
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(url, { headers });
        
        console.log('Respuesta de verificación:', response.data);
        
        // Guardar información del producto
        const pType = response.data.product_type || '';
        const cId = response.data.challenge_id || '';
        const cTitle = response.data.challenge_title || '';
        const needsReg = response.data.needs_registration;
        
        console.log('[WompiSuccess] Datos del pago:', { 
          status: response.data.status,
          product_type: pType, 
          challenge_id: cId, 
          needs_registration: needsReg,
          user_email: response.data.user_email
        });
        
        setProductType(pType);
        setChallengeId(cId);
        setChallengeTitle(cTitle);
        
        if (response.data.status === 'completed') {
          // Verificar si el usuario necesita completar su registro
          const userEmail = response.data.user_email;
          const needsRegistration = response.data.needs_registration;
          
          // Si needsRegistration es true, REDIRIGIR INMEDIATAMENTE sin esperar
          if (needsRegistration) {
            // Usuario pagó como invitado - redirigir directo a crear cuenta
            console.log('[WompiSuccess] Redirigiendo a crear cuenta...');
            navigate(`/complete-registration?reference=${reference || ''}&email=${userEmail || ''}&challenge_id=${cId || ''}&product_type=${pType || ''}`);
            return; // Salir inmediatamente
          } else {
            // Usuario ya tiene cuenta - redirigir al contenido apropiado
            const token = localStorage.getItem('session_token');
            if (token && cId) {
              // Si es un challenge, ir directo al challenge
              console.log('[WompiSuccess] Redirigiendo al challenge...');
              toast.success('¡Pago completado! Redirigiendo...');
              setTimeout(() => navigate(`/challenge/${cId}`), 1500);
              setStatus('success');
            } else if (token) {
              // Si no es challenge, ir al dashboard
              console.log('[WompiSuccess] Redirigiendo al dashboard...');
              toast.success('¡Pago completado exitosamente!');
              setTimeout(() => navigate('/dashboard'), 2000);
              setStatus('success');
            } else {
              setStatus('success');
              toast.success('¡Pago completado exitosamente!');
            }
          }
        } else if (response.data.status === 'failed') {
          setStatus('failed');
          const wompiStatus = response.data.wompi_status;
          let errorMessage = 'El pago fue rechazado por el banco';
          
          if (wompiStatus === 'DECLINED') {
            errorMessage = 'Tu banco rechazó la transacción. Verifica los datos de tu tarjeta o intenta con otro método de pago.';
          } else if (wompiStatus === 'VOIDED') {
            errorMessage = 'La transacción fue cancelada.';
          } else if (wompiStatus === 'ERROR') {
            errorMessage = 'Hubo un error procesando tu pago. Por favor intenta de nuevo.';
          }
          
          setMessage(response.data.message || errorMessage);
          toast.error('El pago no fue completado');
        } else {
          if (attempts < 10) {
            setMessage(`Verificando con el banco... (${attempts + 1}/10)`);
            setTimeout(() => {
              setAttempts(prev => prev + 1);
            }, 3000);
          } else {
            setStatus('pending');
          }
        }
      } catch (error) {
        console.error('Error verifying payment:', error);
        if (attempts < 5) {
          setTimeout(() => {
            setAttempts(prev => prev + 1);
          }, 2000);
        } else {
          // Después de 5 intentos, mostrar estado pendiente - NUNCA asumir éxito
          setStatus('pending');
          setMessage('No pudimos verificar el estado de tu pago. Por favor revisa tu email o contacta soporte.');
        }
      }
    };
    
    verifyPayment();
  }, [paymentIdentifier, wompiId, attempts]);
  
  // VERIFICANDO PAGO
  if (status === 'verifying') {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-4 relative overflow-hidden">
        {/* Efectos de fondo */}
        <div className="absolute inset-0 bg-gradient-to-br from-pink-500/10 via-transparent to-purple-500/10" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-pink-500/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse delay-1000" />
        
        <div className="relative z-10 bg-zinc-900/80 backdrop-blur-xl border border-zinc-800 rounded-3xl p-8 max-w-md w-full text-center">
          <div className="relative w-20 h-20 mx-auto mb-6">
            <div className="absolute inset-0 bg-pink-500/20 rounded-full animate-ping" />
            <div className="relative w-20 h-20 bg-gradient-to-br from-pink-500 to-purple-500 rounded-full flex items-center justify-center">
              <Loader2 className="w-10 h-10 text-white animate-spin" />
            </div>
          </div>
          
          <h1 className="text-2xl font-bold text-white mb-2">Verificando tu pago...</h1>
          <p className="text-zinc-400 mb-4">Por favor espera mientras confirmamos tu transacción</p>
          
          {message && (
            <p className="text-sm text-pink-400 animate-pulse mb-4">{message}</p>
          )}
          
          <div className="bg-zinc-800/50 rounded-xl p-3 mb-4">
            <p className="text-xs text-zinc-500">Referencia: <span className="text-zinc-300 font-mono">{reference}</span></p>
          </div>
          
          <p className="text-xs text-zinc-500">Esto puede tomar hasta 30 segundos para pagos PSE</p>
        </div>
      </div>
    );
  }
  
  // PAGO EXITOSO
  if (status === 'success') {
    // Determinar destino y mensaje según tipo de producto
    const isChallenge = productType === 'challenge' || productType === 'challenge_enrollment' || reference?.startsWith('challenge_');
    const isAcademia = productType === 'academia' || reference?.startsWith('academia_');
    
    const getDestination = () => {
      if (isChallenge && challengeId) {
        return `/challenge/${challengeId}`;
      }
      if (isAcademia) {
        return '/academia';
      }
      return '/dashboard';
    };
    
    const getButtonText = () => {
      if (isChallenge) return 'Ir a Mi Challenge';
      if (isAcademia) return 'Ir a la Academia';
      return 'Comenzar Mi Transformación';
    };
    
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-4 relative overflow-hidden">
        {/* Efectos de fondo celebración */}
        <div className="absolute inset-0 bg-gradient-to-br from-pink-500/20 via-transparent to-purple-500/20" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-1/2 bg-gradient-to-b from-pink-500/10 to-transparent" />
        
        {/* Estrellas flotantes */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(20)].map((_, i) => (
            <Sparkles 
              key={i} 
              className={`absolute text-pink-400/30 animate-pulse`}
              style={{
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                width: `${Math.random() * 20 + 10}px`,
                height: `${Math.random() * 20 + 10}px`,
                animationDelay: `${Math.random() * 2}s`
              }}
            />
          ))}
        </div>
        
        <div className="relative z-10 max-w-lg w-full">
          {/* Card principal */}
          <div className="bg-zinc-900/90 backdrop-blur-xl border border-pink-500/30 rounded-3xl p-8 text-center shadow-2xl shadow-pink-500/20">
            {/* Icono de éxito con animación */}
            <div className="relative w-24 h-24 mx-auto mb-6">
              <div className={`absolute inset-0 rounded-full animate-pulse ${isChallenge ? 'bg-gradient-to-br from-orange-500 to-pink-500' : 'bg-gradient-to-br from-pink-500 to-purple-500'}`} />
              <div className="absolute inset-1 bg-zinc-900 rounded-full flex items-center justify-center">
                {isChallenge ? (
                  <Trophy className="w-12 h-12 text-orange-500" />
                ) : (
                  <CheckCircle className="w-12 h-12 text-pink-500" />
                )}
              </div>
              {/* Corona */}
              <Crown className="absolute -top-3 left-1/2 -translate-x-1/2 w-8 h-8 text-amber-400 animate-bounce" />
            </div>
            
            {/* Mensaje de bienvenida */}
            <div className="space-y-2 mb-6">
              <p className={`text-sm font-medium uppercase tracking-wider ${isChallenge ? 'text-orange-400' : 'text-pink-400'}`}>¡Felicidades!</p>
              
              {isChallenge ? (
                <>
                  <h1 className="text-3xl md:text-4xl font-bold text-white">
                    ¡Estás inscrit{userName ? 'a' : 'a'}!
                  </h1>
                  <h2 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-orange-400 via-pink-500 to-purple-500 text-transparent bg-clip-text">
                    {challengeTitle || 'Tu Challenge'}
                  </h2>
                </>
              ) : isAcademia ? (
                <>
                  <h1 className="text-3xl md:text-4xl font-bold text-white">
                    ¡Acceso Desbloqueado!
                  </h1>
                  <h2 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-green-400 via-emerald-500 to-teal-500 text-transparent bg-clip-text">
                    Academia Completa
                  </h2>
                </>
              ) : (
                <>
                  <h1 className="text-3xl md:text-4xl font-bold text-white">
                    ¡Bienvenida a
                  </h1>
                  <h2 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-pink-400 via-pink-500 to-purple-500 text-transparent bg-clip-text">
                    Booty & Legs Academy!
                  </h2>
                </>
              )}
              
              {userName && (
                <p className="text-xl text-zinc-300 mt-2">
                  <Heart className="inline w-5 h-5 text-pink-500 mr-1" />
                  {userName}
                </p>
              )}
            </div>
            
            {/* Mensaje motivacional */}
            <div className={`border rounded-2xl p-4 mb-6 ${isChallenge ? 'bg-gradient-to-r from-orange-500/10 to-pink-500/10 border-orange-500/20' : 'bg-gradient-to-r from-pink-500/10 to-purple-500/10 border-pink-500/20'}`}>
              <p className="text-zinc-300 text-sm leading-relaxed">
                {isChallenge ? (
                  <>
                    Ya eres parte del challenge. Tu transformación comienza ahora.
                    <br/>
                    <span className="text-orange-400 font-medium">¡A darlo todo!</span> 💪
                  </>
                ) : isAcademia ? (
                  <>
                    Tienes acceso a todos los videos de la Academia.
                    <br/>
                    <span className="text-green-400 font-medium">¡Aprende sin límites!</span> 🎓
                  </>
                ) : (
                  <>
                    Tu transformación comienza ahora. Tienes acceso completo a tu contenido exclusivo.
                    <br/>
                    <span className="text-pink-400 font-medium">¡Es hora de brillar!</span> ✨
                  </>
                )}
              </p>
            </div>
            
            {/* Lo que incluye */}
            {!isChallenge && (
              <div className="grid grid-cols-3 gap-3 mb-6">
                <div className="bg-zinc-800/50 rounded-xl p-3">
                  <Dumbbell className="w-6 h-6 text-pink-400 mx-auto mb-1" />
                  <p className="text-xs text-zinc-400">Rutinas</p>
                </div>
                <div className="bg-zinc-800/50 rounded-xl p-3">
                  <Star className="w-6 h-6 text-amber-400 mx-auto mb-1" />
                  <p className="text-xs text-zinc-400">Nutrición</p>
                </div>
                <div className="bg-zinc-800/50 rounded-xl p-3">
                  <Heart className="w-6 h-6 text-red-400 mx-auto mb-1" />
                  <p className="text-xs text-zinc-400">Mindset</p>
                </div>
              </div>
            )}
            
            <p className="text-xs text-zinc-500 mb-6">Referencia: {reference}</p>
            
            {/* Botón principal */}
            <Button 
              onClick={() => navigate(getDestination())}
              className={`w-full text-white rounded-xl py-4 text-lg font-semibold shadow-lg transition-all hover:scale-[1.02] group ${
                isChallenge 
                  ? 'bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 shadow-orange-500/30'
                  : 'bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 shadow-pink-500/30'
              }`}
            >
              {getButtonText()}
              <ArrowRight className="inline ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </div>
      </div>
    );
  }
  
  // NECESITA REGISTRO - Usuario pagó como invitado
  if (status === 'needs_registration') {
    const isChallenge = productType?.toLowerCase()?.includes('challenge') || challengeId;
    
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-pink-500/20 via-transparent to-purple-500/20" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-1/2 bg-gradient-to-b from-pink-500/10 to-transparent" />
        
        <div className="relative z-10 max-w-lg w-full">
          <div className={`bg-zinc-900/90 backdrop-blur-xl border rounded-3xl p-8 text-center shadow-2xl ${isChallenge ? 'border-orange-500/30 shadow-orange-500/20' : 'border-green-500/30 shadow-green-500/20'}`}>
            {/* Icono de éxito */}
            <div className="relative w-20 h-20 mx-auto mb-6">
              <div className={`absolute inset-0 rounded-full animate-pulse ${isChallenge ? 'bg-gradient-to-br from-orange-500 to-pink-500' : 'bg-gradient-to-br from-green-500 to-emerald-500'}`} />
              <div className="absolute inset-1 bg-zinc-900 rounded-full flex items-center justify-center">
                {isChallenge ? (
                  <Trophy className="w-10 h-10 text-orange-500" />
                ) : (
                  <CheckCircle className="w-10 h-10 text-green-500" />
                )}
              </div>
            </div>
            
            <h1 className="text-2xl font-bold text-white mb-2">¡Pago Confirmado!</h1>
            <p className="text-zinc-400 mb-4">
              {isChallenge 
                ? `Tu inscripción al challenge fue exitosa.`
                : 'Tu compra fue exitosa.'
              }
            </p>
            
            {/* Mensaje de redirección automática */}
            <div className="bg-pink-500/20 border border-pink-500/30 rounded-xl p-3 mb-4">
              <p className="text-pink-300 text-sm font-medium flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Redirigiendo para crear tu cuenta...
              </p>
            </div>
            
            {isChallenge && challengeTitle && (
              <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4 mb-4">
                <p className="text-lg font-bold text-orange-400">{challengeTitle}</p>
              </div>
            )}
            
            {reference && <p className="text-xs text-zinc-500 mb-4">Referencia: {reference}</p>}
            
            <Button 
              onClick={() => navigate(`/complete-registration?reference=${reference || ''}&email=${transactionEmail || ''}&challenge_id=${challengeId || ''}&product_type=${productType || ''}`)}
              className={`w-full text-white rounded-xl py-4 text-lg font-semibold shadow-lg transition-all hover:scale-[1.02] group ${
                isChallenge 
                  ? 'bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 shadow-orange-500/30'
                  : 'bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 shadow-pink-500/30'
              }`}
              data-testid="create-account-btn"
            >
              Crear Mi Cuenta Ahora
              <ArrowRight className="inline ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            
            <p className="text-xs text-zinc-500 mt-4">
              ¿Ya tienes cuenta? <button onClick={() => navigate('/login')} className="text-pink-400 hover:text-pink-300">Inicia sesión</button>
            </p>
          </div>
        </div>
      </div>
    );
  }
  
  // PAGO PENDIENTE
  if (status === 'pending') {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 via-transparent to-orange-500/10" />
        
        <div className="relative z-10 bg-zinc-900/90 backdrop-blur-xl border border-amber-500/30 rounded-3xl p-8 max-w-md w-full text-center">
          <div className="relative w-20 h-20 mx-auto mb-6">
            <div className="absolute inset-0 bg-amber-500/20 rounded-full animate-pulse" />
            <div className="relative w-20 h-20 bg-gradient-to-br from-amber-500 to-orange-500 rounded-full flex items-center justify-center">
              <Loader2 className="w-10 h-10 text-white animate-spin" />
            </div>
          </div>
          
          <h1 className="text-2xl font-bold text-white mb-2">Pago en Proceso</h1>
          <p className="text-zinc-400 mb-6">Tu pago está siendo procesado por el banco</p>
          
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 mb-6">
            <p className="text-sm text-amber-300">
              Esto puede tomar unos minutos. Te enviaremos un email cuando se confirme tu pago.
            </p>
          </div>
          
          <p className="text-xs text-zinc-500 mb-6">Referencia: {reference}</p>
          
          <Button 
            onClick={() => navigate('/dashboard')}
            className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-xl py-4"
          >
            Ir a Mi Cuenta
          </Button>
        </div>
      </div>
    );
  }
  
  // PAGO FALLIDO
  return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-red-500/10 via-transparent to-rose-500/10" />
      
      <div className="relative z-10 bg-zinc-900/90 backdrop-blur-xl border border-red-500/30 rounded-3xl p-8 max-w-md w-full text-center">
        <div className="w-20 h-20 bg-gradient-to-br from-red-500 to-rose-500 rounded-full flex items-center justify-center mx-auto mb-6">
          <XCircle className="w-10 h-10 text-white" />
        </div>
        
        <h1 className="text-2xl font-bold text-white mb-2">Pago No Completado</h1>
        <p className="text-zinc-400 mb-6">No pudimos procesar tu pago</p>
        
        <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 mb-6">
          <p className="text-sm text-red-300">
            {message || 'Por favor intenta de nuevo o usa otro método de pago'}
          </p>
        </div>
        
        {reference && <p className="text-xs text-zinc-500 mb-6">Referencia: {reference}</p>}
        
        <div className="space-y-3">
          <Button 
            onClick={() => navigate('/programs')}
            className="w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white rounded-xl py-4"
          >
            Intentar de Nuevo
          </Button>
          <Button 
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            className="w-full text-zinc-400 hover:text-white rounded-xl py-4 border border-zinc-700"
          >
            Ir a Mi Cuenta
          </Button>
        </div>
      </div>
    </div>
  );
};

export default WompiSuccess;
